/**
 * Rax Connections Manager
 *
 * UI-first secret connection flow with provider presets.
 */

import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  raxApi,
  type SecretInfo,
  type SecretActivationStatus,
  type StoreSecretResult,
} from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

type PresetId =
  | "github"
  | "openai"
  | "anthropic"
  | "openrouter"
  | "google"
  | "slack"
  | "notion"
  | "custom";

type ConnectionPreset = {
  id: PresetId;
  label: string;
  description: string;
  type: string;
  defaultName: string;
  placeholder: string;
};

const PRESETS: ConnectionPreset[] = [
  {
    id: "github",
    label: "GitHub",
    description: "Enable repository actions and PR workflows.",
    type: "custom_api_key",
    defaultName: "GITHUB_PAT",
    placeholder: "ghp_... or github_pat_...",
  },
  {
    id: "openai",
    label: "OpenAI",
    description: "Use OpenAI models and tools with your key.",
    type: "openai_api_key",
    defaultName: "OPENAI_API_KEY",
    placeholder: "sk-...",
  },
  {
    id: "anthropic",
    label: "Anthropic",
    description: "Use Anthropic models with your own key.",
    type: "anthropic_api_key",
    defaultName: "ANTHROPIC_API_KEY",
    placeholder: "sk-ant-...",
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    description: "Route model traffic through OpenRouter.",
    type: "openrouter_api_key",
    defaultName: "OPENROUTER_API_KEY",
    placeholder: "sk-or-v1-...",
  },
  {
    id: "google",
    label: "Google Gemini",
    description: "Enable Gemini model access.",
    type: "google_api_key",
    defaultName: "GEMINI_API_KEY",
    placeholder: "AIza...",
  },
  {
    id: "slack",
    label: "Slack",
    description: "Connect Slack workspace tools and messaging actions.",
    type: "custom_api_key",
    defaultName: "SLACK_BOT_TOKEN",
    placeholder: "xoxb-...",
  },
  {
    id: "notion",
    label: "Notion",
    description: "Connect Notion search/write skills for docs and tasks.",
    type: "custom_api_key",
    defaultName: "NOTION_API_KEY",
    placeholder: "secret_...",
  },
  {
    id: "custom",
    label: "Custom",
    description: "Store any other credential securely.",
    type: "custom_api_key",
    defaultName: "CUSTOM_SECRET",
    placeholder: "Paste secret value",
  },
];
const CONNECTION_PRESET_STORAGE_KEY = "rax.connections.preset";

@customElement("rax-connections-manager")
export class RaxConnectionsManager extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .wrap {
        padding: var(--space-6);
        display: grid;
        gap: var(--space-5);
      }
      
      .header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: var(--space-4);
      }
      
      .title {
        font-size: var(--font-xl);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .subtitle {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        margin-top: var(--space-1);
      }
      
      .reload-btn {
        padding: var(--space-2) var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        cursor: pointer;
      }
      
      .reload-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      .preset-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: var(--space-3);
      }
      
      .preset {
        padding: var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        background: var(--bg-elevated);
        text-align: left;
        cursor: pointer;
      }
      
      .preset.active {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 12px var(--accent-glow);
      }
      
      .preset-title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-1);
      }
      
      .preset-desc {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      .form {
        display: grid;
        gap: var(--space-3);
        padding: var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        background: var(--bg-secondary);
      }
      
      .field {
        display: grid;
        gap: var(--space-1);
      }
      
      .field label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .field input {
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-primary);
      }
      
      .field input:focus {
        outline: none;
        border-color: var(--rax-yellow);
      }
      
      .save-btn {
        justify-self: start;
        padding: var(--space-2) var(--space-4);
        border: none;
        border-radius: var(--radius-md);
        background: var(--rax-yellow);
        color: var(--rax-black);
        font-weight: 700;
        cursor: pointer;
      }
      
      .save-btn:disabled {
        opacity: 0.5;
        cursor: default;
      }
      
      .status {
        padding: var(--space-3);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
      }
      
      .status.success {
        background: rgba(34, 197, 94, 0.1);
        border: 1px solid rgba(34, 197, 94, 0.45);
        color: #22c55e;
      }
      
      .status.error {
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.45);
        color: #ef4444;
      }
      
      .list {
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
      
      .list-head,
      .list-row {
        display: grid;
        grid-template-columns: 1.4fr 1fr 1fr auto;
        gap: var(--space-3);
        align-items: center;
        padding: var(--space-3) var(--space-4);
      }
      
      .list-head {
        background: var(--bg-secondary);
        color: var(--text-tertiary);
        font-size: var(--font-xs);
        text-transform: uppercase;
        letter-spacing: 0.4px;
      }
      
      .list-row {
        border-top: 1px solid var(--border-color);
        font-size: var(--font-sm);
      }
      
      .delete-btn {
        padding: var(--space-1) var(--space-2);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-sm);
        background: var(--bg-elevated);
        color: var(--text-secondary);
        cursor: pointer;
      }
      
      .delete-btn:hover {
        border-color: var(--error);
        color: var(--error);
      }
      
      @media (max-width: 760px) {
        .wrap {
          padding: var(--space-4);
        }
      
        .header {
          align-items: flex-start;
          flex-direction: column;
        }
      
        .list-head {
          display: none;
        }
      
        .list-row {
          grid-template-columns: minmax(0, 1fr);
          gap: var(--space-2);
          padding: var(--space-3);
        }
      
        .list-row .cell {
          display: flex;
          justify-content: space-between;
          gap: var(--space-2);
          font-size: var(--font-xs);
          color: var(--text-secondary);
        }
      
        .list-row .cell::before {
          content: attr(data-label);
          color: var(--text-tertiary);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
      
        .list-row .cell.action {
          justify-content: flex-end;
        }
      
        .list-row .cell.action::before,
        .list-row-empty .cell::before {
          content: "";
        }
      }
    `,
  ];

  @state() private loading = true;
  @state() private saving = false;
  @state() private deletingId: string | null = null;
  @state() private secrets: SecretInfo[] = [];
  @state() private selectedPreset: PresetId = "github";
  @state() private secretName = "GITHUB_PAT";
  @state() private secretType = "custom_api_key";
  @state() private secretValue = "";
  @state() private statusText: string | null = null;
  @state() private statusKind: "success" | "error" = "success";

  override connectedCallback() {
    super.connectedCallback();
    this.applyQueuedPreset();
    void this.loadSecrets();
  }

  private get currentPreset(): ConnectionPreset {
    return PRESETS.find((preset) => preset.id === this.selectedPreset) || PRESETS[0];
  }

  private async loadSecrets() {
    this.loading = true;
    this.statusText = null;
    try {
      const res = await raxApi.listSecrets();
      if (res.success && Array.isArray(res.data)) {
        this.secrets = res.data;
      } else {
        this.statusKind = "error";
        this.statusText = res.error || "Failed to load connections.";
      }
    } catch (err) {
      this.statusKind = "error";
      this.statusText = `Failed to load connections: ${String(err)}`;
    } finally {
      this.loading = false;
    }
  }

  private applyPreset(preset: ConnectionPreset) {
    this.selectedPreset = preset.id;
    this.secretType = preset.type;
    this.secretName = preset.defaultName;
    this.statusText = null;
  }

  private applyQueuedPreset() {
    const queued = localStorage.getItem(CONNECTION_PRESET_STORAGE_KEY);
    if (!queued) return;
    localStorage.removeItem(CONNECTION_PRESET_STORAGE_KEY);
    const preset = PRESETS.find((entry) => entry.id === queued);
    if (!preset) return;
    this.applyPreset(preset);
  }

  private async connectSecret() {
    const name = this.secretName.trim();
    const value = this.secretValue.trim();
    if (!name || !value) {
      this.statusKind = "error";
      this.statusText = "Name and secret value are required.";
      return;
    }

    this.saving = true;
    this.statusText = null;
    try {
      const res = await raxApi.storeSecret({
        type: this.secretType,
        name,
        value,
        metadata: { source: "connections_tab", preset: this.selectedPreset },
      });

      if (!res.success || !res.data) {
        this.statusKind = "error";
        this.statusText = res.error || "Could not save connection.";
        return;
      }

      const data = res.data as StoreSecretResult;
      const activationStatus: SecretActivationStatus | undefined = data.activationStatus;
      const warning = typeof data.warning === "string" ? data.warning : null;

      this.statusKind = "success";
      this.statusText =
        activationStatus === "runtime_broker"
          ? "Connected. Stored in runtime broker immediately."
          : activationStatus === "synced_immediate"
            ? "Connected and active now."
            : activationStatus === "synced_deferred"
              ? "Connected. Runtime sync is in progress."
              : "Connected. Saved to vault.";
      if (warning) {
        this.statusText = `${this.statusText} ${warning}`;
      }

      this.secretValue = "";
      await this.loadSecrets();
    } catch (err) {
      this.statusKind = "error";
      this.statusText = `Could not save connection: ${String(err)}`;
    } finally {
      this.saving = false;
    }
  }

  private async deleteSecret(secretId: string) {
    this.deletingId = secretId;
    this.statusText = null;
    try {
      const res = await raxApi.deleteSecret(secretId);
      if (!res.success) {
        this.statusKind = "error";
        this.statusText = res.error || "Failed to delete connection.";
        return;
      }
      await this.loadSecrets();
      this.statusKind = "success";
      this.statusText = "Connection removed.";
    } catch (err) {
      this.statusKind = "error";
      this.statusText = `Failed to delete connection: ${String(err)}`;
    } finally {
      this.deletingId = null;
    }
  }

  override render() {
    return html`
      <section class="wrap">
        <div class="header">
          <div>
            <div class="title">🔐 Secrets & Connections</div>
            <div class="subtitle">
              Add credentials once. Rax stores them in the vault and activates compatible connections.
            </div>
          </div>
          <button class="reload-btn" @click=${() => this.loadSecrets()}>Refresh</button>
        </div>

        <div class="preset-grid">
          ${PRESETS.map(
            (preset) => html`
            <button
              class="preset ${preset.id === this.selectedPreset ? "active" : ""}"
              @click=${() => this.applyPreset(preset)}
            >
              <div class="preset-title">${preset.label}</div>
              <div class="preset-desc">${preset.description}</div>
            </button>
          `,
          )}
        </div>

        <div class="form">
          <div class="field">
            <label>Connection Name</label>
            <input
              .value=${this.secretName}
              @input=${(e: Event) => (this.secretName = (e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="field">
            <label>Secret Value</label>
            <input
              type="password"
              .placeholder=${this.currentPreset.placeholder}
              .value=${this.secretValue}
              @input=${(e: Event) => (this.secretValue = (e.target as HTMLInputElement).value)}
            />
          </div>
          <button class="save-btn" ?disabled=${this.saving} @click=${this.connectSecret}>
            ${this.saving ? "Saving..." : "Save Connection"}
          </button>
        </div>

        ${
          this.statusText
            ? html`<div class="status ${this.statusKind}">${this.statusText}</div>`
            : html`
                
              `
        }

        <div class="list">
          <div class="list-head">
            <span class="head-col">Name</span>
            <span class="head-col">Type</span>
            <span class="head-col">Updated</span>
            <span class="head-col">Action</span>
          </div>
          ${
            this.loading
              ? html`
                  <div class="list-row list-row-empty"><span class="cell">Loading connections...</span></div>
                `
              : this.secrets.length === 0
                ? html`
                    <div class="list-row list-row-empty"><span class="cell">No saved connections yet.</span></div>
                  `
                : this.secrets.map(
                    (secret) => html`
                  <div class="list-row">
                    <span class="cell" data-label="Name">${secret.name}</span>
                    <span class="cell" data-label="Type">${secret.type}</span>
                    <span class="cell" data-label="Updated">${new Date(secret.updatedAt).toLocaleString()}</span>
                    <div class="cell action" data-label="Action">
                      <button
                        class="delete-btn"
                        ?disabled=${this.deletingId === secret.id}
                        @click=${() => this.deleteSecret(secret.id)}
                      >
                        ${this.deletingId === secret.id ? "..." : "Delete"}
                      </button>
                    </div>
                  </div>
                `,
                  )
          }
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-connections-manager": RaxConnectionsManager;
  }
}
